<template>
    <div class="a-label" :class="classNames">
        <a-dynamic-link
            v-if="to"
            :to="to"
            class="content"
            :target="target"
        >
            <a-glyph v-if="glyph" :name="glyph" :size="glyphSize" />
            <slot />
        </a-dynamic-link>
        <div v-else class="content">
            <a-glyph v-if="glyph" :name="glyph" :size="glyphSize" />
            <slot />
        </div>
    </div>
</template>

<script lang="ts">
import ADynamicLink from '@core/components/dynamic-link/dynamic-link.vue';
import AGlyph from '@core/components/glyph/glyph.vue';
import { TYPES, VARIANTS, LAYOUTS, SIZES, THEMES } from './constants.js';

export default {
    name: 'ALabel',

    components: {
        AGlyph,
        ADynamicLink,
    },

    props: {
        type: {
            type: String,
            default: 'label',
            validator: (value) => TYPES.includes(String(value)),
        },

        variant: {
            type: String,
            default: 'info',
            validator: (value) => VARIANTS.includes(String(value)),
        },

        layout: {
            type: String,
            default: 'solid',
            validator: (value) => LAYOUTS.includes(String(value)),
        },

        size: {
            type: String,
            default: 'l',
            validator: (value) => SIZES.includes(String(value)),
        },

        to: {
            type: String,
            default: undefined,
        },

        target: {
            type: String,
            default: '_self',
        },

        theme: {
            type: String,
            default: 'light',
            validator: (value) => THEMES.includes(String(value)),
        },

        glyph: {
            type: String,
            default: '',
        },
    },

    computed: {
        classNames() {
            return {
                [`type-${this.type}`]: this.type,
                [`variant-${this.variant}`]: this.variant,
                [`layout-${this.layout}`]: this.layout,
                [`size-${this.size}`]: this.size,
                [`theme-${this.theme}`]: this.theme,
            };
        },

        glyphSize() {
            if (this.size === 'xl') return 'm';
            if (this.type === 'label') return 's';

            return this.size === 'l' ? 'm' : 's';
        },
    },
};
</script>

<style lang="postcss" scoped>
.a-label {
    display: inline-block;
    position: relative;
    z-index: 1;

    .content {
        position: relative;
        z-index: 3;
        display: flex;
        justify-content: center;
        align-items: center;
    }

    :deep(.a-glyph) {
        flex-shrink: 0;
        margin-inline-end: 8px;
        &:not(.a-glyph_noFill) {
            color: inherit;
            fill: currentcolor;
        }
    }

    &.size-s {
        :deep(.a-glyph) {
            margin-inline-end: 6px;
        }
    }
}

.a-label.type-label {
    @mixin note-heading;
    box-sizing: border-box;
    font-weight: 700;
    padding: 4px 12px;
    border-radius: 16px;
    color: var(--av-nav-primary);
    background: var(--label-fill, transparent);
    box-shadow: inset 0 0 0 var(--border-width) var(--label-border, transparent);

    --border-width: 2px;
    --label-fill: transparent;
    --label-border: transparent;

    :deep(.a-glyph) {
        max-width: 16px;
        max-height: 16px;
    }

    &.size-s {
        padding: 0 8px;
        border-radius: 8px;

        --border-width: 1px;

        .a-glyph {
            width: 12px;
            height: 12px;
        }
    }

    &.size-xl {
        font-size: 16px;
        line-height: 20px;
        padding: 4px 12px;

        .content:has(> .a-glyph) {
            min-height: 24px;
        }
    }

    &.layout-subtle.variant-ai-gradient {
        box-shadow: none;

        &::before {
            content: '';
            position: absolute;
            inset: 0;
            padding: var(--border-width);
            border-radius: inherit;
            background: var(--av-gradient-ai-powered);
            mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
            mask-composite: exclude;
            pointer-events: none;
        }
    }

    /* SOLID — on light background */
    &.layout-solid.theme-light {
        color: var(--av-nav-primary);

        &.variant-info {
            --label-fill: var(--av-solid-brand-accent);
            --label-border: var(--av-brand-secondary-light);
        }

        &.variant-success {
            --label-fill: #d2ffe0;
            --label-border: rgba(22, 163, 74, 0.6);
        }

        &.variant-neutral {
            --label-fill: var(--av-fixed-neutral-accent);
            --label-border: var(--av-fixed-invisible);
            color: var(--av-fixed-neutral-dark);
        }

        &.variant-tru {
            --label-fill: #ffcfcf;
            --label-border: rgba(193, 43, 34, 0.3);
        }

        &.variant-ai-gradient {
            --label-fill: var(--av-gradient-ai-powered);
            color: var(--av-inversed-primary);
        }

        &.variant-lime {
            --label-fill: rgba(71, 255, 147, 1);
            color: var(--av-nav-primary);
        }
    }

    /* SOLID — on dark background */
    &.layout-solid.theme-dark {
        color: var(--av-inversed-primary);

        &.variant-info {
            --label-fill: var(--av-nav-secondary);
        }

        &.variant-success {
            --label-fill: #23e172;
            color: var(--av-nav-primary);
        }

        &.variant-neutral {
            --label-fill: var(--av-fixed-neutral-dark);
        }

        &.variant-tru {
            --label-fill: #c12b22;
        }

        &.variant-ai-gradient {
            --label-fill: var(--av-gradient-ai-powered);
        }

        &.variant-lime {
            --label-fill: rgba(71, 255, 147, 1);
            color: var(--av-nav-primary);
        }
    }

    /* SUBTLE — on light background */
    &.layout-subtle.theme-light {
        --label-fill: var(--av-inversed-primary);
        color: var(--av-nav-primary);

        &.variant-info {
            --label-border: var(--av-nav-secondary);
        }

        &.variant-success {
            --label-border: #16a34a;
        }

        &.variant-neutral {
            --label-border: var(--av-fixed-neutral);
            color: var(--av-fixed-neutral-dark);
        }

        &.variant-tru {
            --label-border: #c12b22;
        }

        &.variant-lime {
            --label-fill: rgba(1, 101, 73, 1);
            --label-border: rgba(71, 255, 147, 1);
            color: var(--av-inversed-primary);
        }
    }

    /* SUBTLE — on dark background */
    &.layout-subtle.theme-dark {
        --label-fill: var(--av-nav-primary);
        color: var(--av-inversed-primary);

        &.variant-info {
            --label-border: var(--av-brand-secondary);
        }

        &.variant-success {
            --label-border: #23e172;
        }

        &.variant-neutral {
            --label-border: var(--av-fixed-neutral);
            color: var(--av-fixed-neutral-light);
        }

        &.variant-tru {
            --label-border: #c12b22;
            color: var(--av-fixed-neutral-light);
        }

        &.variant-lime {
            --label-fill: rgba(1, 101, 73, 1);
            --label-border: rgba(71, 255, 147, 1);
            color: var(--av-inversed-primary);
        }
    }
}

.a-label.type-text-label {
    @mixin paragraph-accent;
    color: var(--av-nav-secondary);

    :deep(.a-glyph) {
        width: 24px;
        height: 24px;
    }

    &.size-s {
        @mixin body-accent;

        :deep(.a-glyph) {
            width: 16px;
            height: 16px;
        }
    }

    &.size-xl {
        @mixin lead-accent;
    }

    &.theme-light {
        &.variant-info {
            color: var(--av-nav-secondary);
        }

        &.variant-success {
            color: #067f40;
        }

        &.variant-neutral {
            color: var(--av-fixed-neutral-dark);
        }

        &.variant-tru {
            color: #c12b22;
        }

        &.variant-ai-gradient {
            color: var(--av-nav-primary);
        }
    }

    &.theme-dark {
        &.variant-info {
            color: var(--av-brand-secondary);
        }

        &.variant-success {
            color: #23e172;
        }

        &.variant-neutral {
            color: var(--av-inversed-light);
        }

        &.variant-tru {
            color: var(--av-fixed-danger);
        }

        &.variant-ai-gradient {
            color: var(--av-inversed-primary);
        }
    }
}

</style>
